import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { NearViewMap } from "./near-view-map";
import { PW, pewterButton, pewterPanelApproved } from "../internal-ui/pewter-shell";

/** Google Maps turn-by-turn routing to a pin, opened in a new tab. */
export function directionsUrl(latitude: number | string, longitude: number | string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
}

/**
 * The Directions control: white with one hairline, like every control on a
 * pewter surface (controls never wear the band). Shared by this card and the
 * restaurant MapCard so the two read as the same button.
 */
export function DirectionsLink({
  latitude,
  longitude,
  className,
}: {
  latitude: number | string;
  longitude: number | string;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <a
      href={directionsUrl(latitude, longitude)}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-full px-4 font-sans text-[13px] font-semibold transition-transform duration-150 active:scale-[0.97] ${className ?? ""}`}
      style={{ ...pewterButton, color: PW.navy }}
    >
      <Icon name="ri-navigation-line" className="text-[15px]" aria-hidden="true" />
      {t("activity.restaurant.directions")}
    </a>
  );
}

export type PickupTag = "none" | "available" | null;

/* The same rule the Good-to-know tab uses to turn "Pickup" into "Meeting
   point": an excluded line that names pickup, transfer or transport. */
const NO_PICKUP_RE = /\b(pick[-\s]?up|transfer|transport(ation)?)\b/i;

function excludedLines(blocks: Record<string, unknown> | null | undefined, lang: string): string[] {
  const block = blocks?.excluded as Record<string, unknown> | undefined;
  if (!block || typeof block !== "object") return [];
  const base = lang.split("-")[0] ?? "en";
  // English first: the rule's words are English, and every row has an en list.
  const lists = [block.en, block[base]];
  return lists
    .flatMap((list) => (Array.isArray(list) ? list : []))
    .filter((it): it is string => typeof it === "string");
}

/**
 * What the card may say about pickup, from real data only:
 * - "none" when the activity's own "Not included" list names pickup;
 * - "available" when any package prices a pickup;
 * - null (say nothing) otherwise.
 */
export function pickupTagFor(
  blocks: Record<string, unknown> | null | undefined,
  packages: ReadonlyArray<{ pricing?: { transport?: number | null } | null }> | null | undefined,
  lang = "en",
): PickupTag {
  if (excludedLines(blocks, lang).some((line) => NO_PICKUP_RE.test(line))) return "none";
  if ((packages ?? []).some((p) => (p.pricing?.transport ?? 0) > 0)) return "available";
  return null;
}

export interface LocationCardProps {
  latitude: number | string;
  longitude: number | string;
  /** The area line: the city today. */
  place: string;
  /** The activity's content blocks, read for a "no pickup" line. */
  blocks?: Record<string, unknown> | null;
  /** The activity's packages, read for a priced pickup. */
  packages?: ReadonlyArray<{ pricing?: { transport?: number | null } | null }> | null;
  className?: string;
}

/**
 * "#22 Where it starts" from the pewter lab (owner pick), for activities
 * (restaurants keep MapCard beside their hours): the map inside the pewter
 * panel, the place, whether there is a hotel pickup, and Directions.
 *
 * The pickup answer is only shown when the data says so; with nothing to go
 * on the card says nothing rather than guess.
 */
export function LocationCard({ latitude, longitude, place, blocks, packages, className }: LocationCardProps) {
  const { t, i18n } = useTranslation();
  const pickup = pickupTagFor(blocks, packages, i18n.language);

  return (
    <section
      aria-labelledby="location-card-title"
      data-testid="location-card"
      className={`p-2 ${className ?? ""}`}
      style={pewterPanelApproved(22)}
    >
      <div className="flex flex-wrap items-end justify-between gap-3 px-2.5 pb-3 pt-2">
        <div className="min-w-0">
          <h2
            id="location-card-title"
            className="font-heading text-[20px] font-bold md:text-[22px]"
            style={{ color: PW.navy }}
          >
            {t("activity.location")}
          </h2>
          <p className="mt-0.5 flex min-w-0 items-center gap-1.5 font-sans text-[13px]" style={{ color: PW.muted }}>
            <Icon name="ri-map-pin-line" className="shrink-0 text-[14px]" aria-hidden="true" />
            <span className="min-w-0 line-clamp-2">{place}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pickup && (
            <span
              data-testid="pickup-tag"
              data-pickup={pickup}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-sans text-[12px] font-semibold"
              style={
                pickup === "none"
                  ? { background: PW.goldTint, color: PW.goldDark }
                  : { background: PW.greenTint, color: PW.green }
              }
            >
              <Icon name="ri-car-line" className="text-[13px]" aria-hidden="true" />
              {pickup === "none" ? t("activity.no_hotel_pickup") : t("activity.hotel_pickup_available")}
            </span>
          )}
          <DirectionsLink latitude={latitude} longitude={longitude} />
        </div>
      </div>
      {/* Words first, then the map (idea 23): where it is shows at once, while
          the map takes its time. The frame clips the map, not the panel, so
          the panel keeps its shadow. */}
      <div className="overflow-hidden rounded-[14px]" style={{ boxShadow: `0 0 0 1px ${PW.hairline}` }}>
        <NearViewMap
          title={t("activity.map")}
          latitude={latitude}
          longitude={longitude}
          className="block h-[240px] w-full border-0 md:h-[280px]"
        />
      </div>
    </section>
  );
}
